import { connect } from 'react-redux';
import { useState, useEffect, useRef, useCallback, } from 'react';

import { setLoading, setAlert, siteboardAction } from '../actions/defaultActions';
import {
  Email, SiteboardApiValidator, INVITE_LINK_EXPIRE_TIME_DAYS, REDEEM_SHARE_SCHEDULE_LINK, SETUP_LOCAL_SESSION, User, Subscription, REDEEM_SHARE_SCHEDULE_LINK_INVITE,
  SET_SHARE_SCHEDULE_CONFIRMED,
} from '../actions/types';
import { getServerApiResponse, uuidv4, } from '../Util';

import Schedule from './Schedule';
import PoweredByBar from './PoweredByBar';
import ParallaxSection from './ParallaxSection';
import PushButton from './PushButton';

import Logo from '../SiteBoard_Logo';

//const testMode = false;

const SharedSchedulePage = ({_shareScheduleCode, _localEditScheduleId, _localEditScheduleProjectId, _localOnlyMode, _shareScheduleConfirmed, setAlert, setLoading, siteboardAction}) => {
  const inputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]';
  const disabledInputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] focus:bg-[#656565]';

  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [fetchScheduleErrorMessage, setFetchScheduleErrorMessage] = useState('');

  const [inputFirstName, setInputFirstName] = useState('');
  const [inputLastName, setInputLastName] = useState('');
  const [inputCompanyName, setInputCompanyName] = useState('');
  const [inputEmail, setInputEmail] = useState('');

  const [needContactInfo, setNeedContactInfo] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactCompanyName, setContactCompanyName] = useState('');

  const fetchScheduleState = useRef('none');

  const fetchSchedule = useCallback(() => {
    const onApiSuccess = (data) => {
      try {
        fetchScheduleState.current = 'complete';
        const validOutput = SiteboardApiValidator[REDEEM_SHARE_SCHEDULE_LINK].validateOutput(data); 
        if(validOutput.needContactInfo === true) {
          setNeedContactInfo(true);
          setInputEmail(validOutput.contactEmail);
        } else {
          const currentTimestamp = new Date().getTime();
          const subscriptionId = uuidv4();
          const user = User.validate({
            id: uuidv4(),
            email: 'trial@test',
            firstName: 'trial',
            lastName: 'test',
            projects: {[validOutput.project.id]: ''},
            isOnline: true,
            lastOnlineTimestamp: currentTimestamp,
            ownedSubscriptionId: subscriptionId,
            subscriptions: {[subscriptionId]: ''},
          });
          const subscription = Subscription.validate({
            id: subscriptionId,
            type: 'Trial',
            members: {[user.id]: {
              role: 'Member', // so editing the schedule is disabled, change to 'Owner' to enable editing
              modifiedTimestamp: currentTimestamp,
              createdTimestamp: currentTimestamp,
            }},
            projectIds: {[validOutput.project.id]: ''},
            userCount: 1,
            projectCount: 1,
            scheduleCount: 1,
            contactCount: 0,
            endTimestamp: currentTimestamp,
            createdTimestamp: currentTimestamp,
            modifiedTimestamp: currentTimestamp,
          });
          validOutput.project.subscriptionId = subscriptionId;

          siteboardAction(SETUP_LOCAL_SESSION, {
            userId: user.id,
            users: [user],
            activities: validOutput.activities,
            projects: [validOutput.project],
            schedules: [validOutput.schedule],
            subscriptions: [subscription],
            userPreferences: {
              userId: user.id,
              darkMode: true,
            },
          });
          siteboardAction(SET_SHARE_SCHEDULE_CONFIRMED, { isConfirmed: validOutput.isConfirmed });
          setContactEmail(validOutput.contactEmail);
          setContactFirstName(validOutput.contactFirstName);
          setContactLastName(validOutput.contactLastName);
          setContactCompanyName(validOutput.contactCompanyName);
        }
        setScheduleLoading(false);
      } catch(error) {
        onApiError(error.message);
      }
    };
    const onApiError = (errorMessage) => {
      fetchScheduleState.current = 'none';
      setScheduleLoading(false);
      setFetchScheduleErrorMessage(typeof errorMessage === 'string' ? errorMessage : 'error getting schedule');
      //setAlert(typeof errorMessage === 'string' ? errorMessage : 'error getting schedule');
    };
    setScheduleLoading(true);
    getServerApiResponse({ 
      action: REDEEM_SHARE_SCHEDULE_LINK,
      shareCode: _shareScheduleCode,
    }, onApiSuccess, onApiError);

    fetchScheduleState.current = 'pending';
  }, [_shareScheduleCode, siteboardAction, ]);

  useEffect(() => {
    //if(testMode) {
    //  setScheduleLoading(false);
    //  return;
    //}

    if(fetchScheduleState.current !== 'none') return;
    fetchSchedule();
  }, [fetchSchedule]);

  const acceptInvite = () => {
    let apiArgs = null;
    try {
      apiArgs = SiteboardApiValidator[REDEEM_SHARE_SCHEDULE_LINK_INVITE].validateInput({
        action: REDEEM_SHARE_SCHEDULE_LINK_INVITE,
        shareCode: _shareScheduleCode,
        firstName: inputFirstName,
        lastName: inputLastName,
        companyName: inputCompanyName,
      });
      if(apiArgs.firstName.length === 0) throw new Error('Please enter your first name');
      if(apiArgs.lastName.length === 0) throw new Error('Please enter your last name');
      if(apiArgs.companyName.length === 0) throw new Error('Please enter your company name');
    } catch(error) {
      setAlert(error.message);
      return;
    }

    const onApiSuccess = (data) => {
      setLoading('');
      try {
        const validOutput = SiteboardApiValidator[REDEEM_SHARE_SCHEDULE_LINK_INVITE].validateOutput(data); 
        fetchSchedule();
      } catch(error) {
        onApiError(error.message);
      }
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'There was an error accepting your invite.');
    };
    setLoading('Confirming Your Info');
    getServerApiResponse(apiArgs, onApiSuccess, onApiError);
  };

  return (
    <>
      {(scheduleLoading === false && _localOnlyMode === true) ?
          <div className='print:block print:w-full print:h-auto flex flex-col xl:flex-row w-screen h-screen overflow-hidden'>
            <Schedule 
             projectId={_localEditScheduleProjectId}
             scheduleId={_localEditScheduleId}
             sharedScheduleIsConfirmed={_shareScheduleConfirmed}
             sharedScheduleCode={_shareScheduleCode}
             sharedScheduleContactEmail={contactEmail}
             sharedScheduleContactFirstName={contactFirstName}
             sharedScheduleContactLastName={contactLastName}
             sharedScheduleContactCompanyName={contactCompanyName}
            />
          </div>
        :
          <div className={`relative flex flex-col justify-between min-h-screen bg-[#151515] text-white overflow-hidden`}>
            <div className='relative z-[1] grow flex flex-col justify-center items-center max-w-screen-sm px-2 py-6 text-[1.2em] w-full mx-auto'>
              <div className='grow flex flex-col justify-center items-center'>
                <div className='flex flex-col gap-6'>
                  {/* SiteBoard logo */}
                  <div className='flex flex-col justify-center items-center'>
                    <a href='/' className={'relative left-[-0.3em] flex items-center gap-x-2 font-bold text-[1.8em]'}>
                      <div className={`${scheduleLoading ? 'animate-[spin_1s_linear_infinite]' : ''}`}>
                        <Logo size={'1em'} />
                      </div>
                      <div className='flex flex-col'>
                      {process.env.REACT_APP_WEBSITE_NAME} 
                      </div>
                    </a>
                    {scheduleLoading === true && 
                      <div className='font-medium text-[#aaa] text-[1.2em]'>Loading Schedule</div>
                    }
                    {scheduleLoading === false && needContactInfo &&
                      <div className='flex flex-col gap-4 bg-[#44444466] rounded-lg p-4'>
                        <div className='font-medium'>Please confirm the information below to view the schedule</div>
                        <div className='flex flex-col gap-y-6'>
                          {/* First and Last name */}
                          <div className='flex gap-x-2'>
                            <div>
                              <label className='block text-sm font-medium mb-1'>First Name</label>
                              <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                                <input 
                                  value={inputFirstName}
                                  className={inputStyle}
                                  type='text' 
                                  onChange={(event) => { setInputFirstName(event.target.value); }}
                                />
                              </div>
                            </div>
                            <div>
                              <label className='block text-sm font-medium mb-1'>Last Name</label>
                              <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                                <input 
                                  value={inputLastName}
                                  className={inputStyle}
                                  type='text' 
                                  onChange={(event) => { setInputLastName(event.target.value); }}
                                />
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className='block text-sm font-medium mb-1'>Company Name</label>
                            <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                              <input 
                                value={inputCompanyName}
                                className={inputStyle}
                                type='text' 
                                onChange={(event) => { setInputCompanyName(event.target.value); }}
                              />
                            </div>
                          </div>
                          {/* Email Input */}
                          <div>
                            <label className='block text-sm font-medium mb-1'>Email</label>
                            <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                              <input 
                                value={inputEmail}
                                className={disabledInputStyle}
                                type='text' 
                                disabled={true}
                                onChange={(event) => {}}
                              />
                            </div>
                          </div>
                          {/* submit email button */}
                          <PushButton 
                            textColor='black'
                            bgColor='#f9a928'
                            hoverBgColor='#f79800'
                            onClick={() => { acceptInvite(); }}
                          >
                            Confirm & View Schedule
                          </PushButton>
                        </div>
                      </div>
                    }
                    {scheduleLoading === false && fetchScheduleErrorMessage.length > 0 &&
                      <div className='text-center'>{fetchScheduleErrorMessage}</div>
                    }
                  </div>
                </div>
              </div>
            </div>
            <div className='z-[1]'>
              <PoweredByBar />
            </div>
            {/* construction site background */}
            <div className='z-[0] absolute top-[0vh] left-[0vw] opacity-[1] pointer-events-none'>
              <ParallaxSection parallaxMultiplier={0.5}>
                {/*<img width='906' height='1000' className='h-[120vh] object-cover object-top' src='/images/constructionSite2.svg' alt='line drawing of a construction site' />*/}
                <img width='1536' height='1024' className='w-[100vw] min-h-[100vh] object-cover object-top' src='/images/constructionSite3.jpg' alt='a construction site' />
                {/* gradient at bottom to fade image into gray background */}
                <div className='absolute bottom-0 left-0 h-[15vh] w-[100vw] bg-gradient-to-t from-[#151515] to-[#15151500]'></div>
              </ParallaxSection>
            </div>
            {/* top right floating logo */}
            <div className='fixed top-[4em] left-[100%] translate-x-[-90%] opacity-[0.10] pointer-events-none'>
              <div className='sm:hidden'><Logo size={250} /></div>
              <div className='hidden sm:block 2xl:hidden'><Logo size={300} /></div>
              <div className='hidden 2xl:block'><Logo size={400} /></div>
            </div>
          </div>
      }
    </>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _shareScheduleCode: state.localEphemeral.shareScheduleCode,
  _localEditScheduleId: state.serverSynced.localEditScheduleId,
  _localEditScheduleProjectId: state.serverSynced.localEditScheduleProjectId,
  _localOnlyMode: state.serverSynced.localOnlyMode,
  _shareScheduleConfirmed: state.localEphemeral.shareScheduleConfirmed,
}};
export default connect(mapStateToProps, {setAlert, setLoading, siteboardAction})(SharedSchedulePage);
